import { useState, useEffect } from "react";
import { expenseTypesAPI } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  DollarSign,
  Fuel,
  Car,
  Wrench,
  ParkingCircle
} from "lucide-react";

const CATEGORY_ICONS = {
  gasolina: Fuel,
  casetas: Car,
  mantenimiento: Wrench,
  estacionamiento: ParkingCircle,
  default: DollarSign
};

export function ExpenseTypesManager() {
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    category: "",
    description: "",
    is_active: true
  });

  useEffect(() => {
    fetchExpenseTypes();
  }, []);

  const fetchExpenseTypes = async () => {
    setLoading(true);
    try {
      const response = await expenseTypesAPI.getAll();
      setExpenseTypes(response.data || []);
    } catch (error) {
      console.error("Error loading expense types:", error);
      toast.error("Error al cargar tipos de gasto");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error("Nombre y código son requeridos");
      return;
    }
    
    try {
      if (editingType) {
        await expenseTypesAPI.update(editingType.id, formData);
        toast.success("Tipo de gasto actualizado");
      } else {
        await expenseTypesAPI.create(formData);
        toast.success("Tipo de gasto creado");
      }
      setDialogOpen(false);
      resetForm();
      fetchExpenseTypes();
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al guardar";
      toast.error(msg);
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name || "",
      code: type.code || "",
      category: type.category || "",
      description: type.description || "",
      is_active: type.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (typeId) => {
    if (!window.confirm("¿Desactivar este tipo de gasto?")) return;
    
    try {
      await expenseTypesAPI.delete(typeId);
      toast.success("Tipo de gasto desactivado");
      fetchExpenseTypes();
    } catch (error) {
      toast.error("Error al desactivar tipo de gasto");
    }
  };

  const resetForm = () => {
    setEditingType(null);
    setFormData({
      name: "",
      code: "",
      category: "",
      description: "",
      is_active: true
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const generateCode = (name) => {
    return name.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="expense-types-manager">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Tipos de Gasto</h3>
          <p className="text-sm text-slate-500">Catálogo para clasificar gastos operativos por unidad</p>
        </div>
        <Button onClick={openNewDialog} className="gap-2" data-testid="add-expense-type-btn">
          <Plus className="w-4 h-4" />
          Nuevo Tipo
        </Button>
      </div>

      {expenseTypes.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No hay tipos de gasto configurados</p>
          <Button variant="link" onClick={openNewDialog}>Crear el primero</Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Código</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenseTypes.map((type) => {
              const IconComponent = CATEGORY_ICONS[type.code] || CATEGORY_ICONS.default;
              return (
                <TableRow key={type.id} data-testid={`expense-type-row-${type.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="font-medium">{type.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded">{type.code}</code>
                  </TableCell>
                  <TableCell>{type.category || "-"}</TableCell>
                  <TableCell>
                    <Badge className={type.is_active !== false ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                      {type.is_active !== false ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(type)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(type.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Dialog for Add/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingType ? "Editar Tipo de Gasto" : "Nuevo Tipo de Gasto"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setFormData(prev => ({ 
                    ...prev, 
                    name,
                    code: editingType ? prev.code : generateCode(name)
                  }));
                }}
                placeholder="Ej: Gasolina, Casetas, Estacionamiento"
                required
                data-testid="expense-type-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="code">Código único *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toLowerCase().replace(/\s+/g, "_") }))}
                placeholder="gasolina"
                required
                data-testid="expense-type-code"
              />
              <p className="text-xs text-slate-500">Identificador único, sin espacios</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Categoría (opcional)</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="Ej: Operativo, Administrativo"
                data-testid="expense-type-category"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Descripción (opcional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Breve descripción del tipo de gasto"
                data-testid="expense-type-description"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-expense-type-btn">
                {editingType ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ExpenseTypesManager;
